import { BaseService } from './BaseService';
import type {
  UserSettingsDto,
  UpdateUserSettingsRequest,
  TenantSettingsDto,
  ProjectSettingsDto,
  UpdateProjectSettingsRequest,
  SystemConfigurationDto,
  UpdateSystemConfigurationRequest,
  EffectiveSettingsDto,
} from '../types/settings';

/**
 * Settings service for handling user/tenant/project settings and system configuration.
 */
export class SettingsService extends BaseService {
  /**
   * Gets settings for the current user.
   */
  async getMyUserSettings(): Promise<UserSettingsDto> {
    return this.get<UserSettingsDto>('/users/me/settings');
  }

  /**
   * Updates settings for the current user.
   */
  async updateMyUserSettings(data: UpdateUserSettingsRequest): Promise<UserSettingsDto> {
    return this.put<UserSettingsDto>('/users/me/settings', data);
  }

  /**
   * Gets settings for a specific tenant (read-only).
   */
  async getTenantSettings(tenantId: string): Promise<TenantSettingsDto> {
    return this.get<TenantSettingsDto>(`/tenants/${tenantId}/settings`);
  }

  /**
   * Gets settings for a project.
   * Note: Backend requires tenant context (X-Tenant-Id header is sent automatically by apiClient).
   */
  async getProjectSettings(projectId: string): Promise<ProjectSettingsDto> {
    return this.get<ProjectSettingsDto>(`/projects/${projectId}/settings`);
  }

  /**
   * Updates settings for a project.
   * Note: Backend requires tenant context (X-Tenant-Id header is sent automatically by apiClient).
   */
  async updateProjectSettings(projectId: string, data: UpdateProjectSettingsRequest): Promise<ProjectSettingsDto> {
    return this.put<ProjectSettingsDto>(`/projects/${projectId}/settings`, data);
  }

  /**
   * Lists all system configuration entries (AdminOnly).
   */
  async getSystemConfigurations(): Promise<SystemConfigurationDto[]> {
    return this.get<SystemConfigurationDto[]>('/system/configurations');
  }

  /**
   * Gets a system configuration by key (AdminOnly).
   */
  async getSystemConfigurationByKey(key: string): Promise<SystemConfigurationDto> {
    return this.get<SystemConfigurationDto>(`/system/configurations/${encodeURIComponent(key)}`);
  }

  /**
   * Updates (or creates) a system configuration by key (AdminOnly).
   */
  async updateSystemConfiguration(key: string, data: UpdateSystemConfigurationRequest): Promise<SystemConfigurationDto> {
    return this.put<SystemConfigurationDto>(`/system/configurations/${encodeURIComponent(key)}`, data);
  }

  /**
   * Gets effective settings for the current user and current tenant/project context.
   */
  async getEffectiveSettings(): Promise<EffectiveSettingsDto> {
    return this.get<EffectiveSettingsDto>('/settings/effective');
  }
}

export const settingsService = new SettingsService();

